import { MessageBarType } from "@fluentui/react";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from "react";

import { useAlert } from "./useAlert";

import { AlertProps, AlertTypes } from "@/common/types/AlertTypes";
import { useGetProfile } from "@/core/libs/api/react-query";
import { BusinessUnit, BusinessUnitRegion } from "@/modules/User/types/types";
import { destructureAllBusinessUnitsChildren } from "@/utils/DestructureAllBussinesUnitsChildren";

interface SelectedBuContextData {
  handleSelectBu: (id: number) => void;
  handleSelectRegion: (region: string) => void;
  handleSelectSegment: (segment: string, region: string) => void;
  selectedBU?: BusinessUnit;
  selectedRegion?: string;
  selectedSegment?: string;
}

const SelectedBuContext = createContext<SelectedBuContextData>(
  {} as SelectedBuContextData
);

/**
 * Dado um id de unidade de negócio e a árvore de classificação,
 * encontrar qual região e segmento contém ela, buscando recursivamente
 * pelos filhos.
 */
function findRegionAndSegmentForBusinessUnit(
  buId: number,
  classification?: BusinessUnitRegion[]
): { region: string; segment: string } | null {
  if (!classification) return null;

  const hasBusinessUnit = (bus: BusinessUnit[]): boolean =>
    bus?.some(bu => bu.id === buId || hasBusinessUnit(bu.children));

  for (const { region, segments } of classification) {
    for (const { segment, businessUnits } of segments) {
      if (hasBusinessUnit(businessUnits)) {
        return { region, segment };
      }
    }
  }

  return null;
}

export const SelectedBuProvider = ({ children }: { children: ReactNode }) => {
  const userProfile = useGetProfile();
  const [selectedBU, setSelectedBU] = useState<BusinessUnit>(null);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [selectedSegment, setSelectedSegment] = useState<string | null>(null);

  const profileData = userProfile?.data?.data;

  const findBuByIdInProfile = useCallback(
    (id: number): BusinessUnit | undefined => {
      if (!profileData) return undefined;

      const allBUs = destructureAllBusinessUnitsChildren(
        profileData.businessUnits,
        profileData.businessUnitClassification
      );

      return allBUs.find(bu => bu.id === id);
    },
    [profileData]
  );

  const isEmpty = (stringToBeParsed?: string): boolean => {
    return !stringToBeParsed || stringToBeParsed === "undefined";
  };

  const setLocalStorage = useCallback(bu => {
    const oldSelected = localStorage.getItem("selectedBU");

    if (isEmpty(oldSelected) || JSON.parse(oldSelected).id === bu?.id) return;

    localStorage.setItem("selectedBU", JSON.stringify(bu));
  }, []);

  const persistRegionSegment = useCallback(
    (region: string | null, segment: string | null) => {
      if (region !== null) {
        localStorage.setItem("selectedRegion", region);
      } else {
        localStorage.removeItem("selectedRegion");
      }

      if (segment !== null) {
        localStorage.setItem("selectedSegment", segment);
      } else {
        localStorage.removeItem("selectedSegment");
      }
    },
    []
  );

  const { createAlert } = useAlert();

  const alert: AlertProps = {
    level: MessageBarType.error,
    type: AlertTypes.NOTIFICATION,
    message: `Sem permissão de acesso, redirecionando para unidade de negócio padrão!`
  };

  const handleSelectBu = useCallback(
    (id: number) => {
      if (id) {
        const bu = findBuByIdInProfile(id);

        if (!bu) {
          return createAlert({
            ...alert,
            level: MessageBarType.error,
            code: 403
          });
        }

        setSelectedBU(bu);
        setLocalStorage(bu);

        // Detectar região e segmento a partir da árvore de classificação
        const classification = profileData?.businessUnitClassification;
        const location = findRegionAndSegmentForBusinessUnit(
          id,
          classification
        );

        if (location) {
          setSelectedRegion(location.region);
          setSelectedSegment(location.segment);
          persistRegionSegment(location.region, location.segment);
        } else {
          // Modo flat (sem classificação), limpar região/segmento
          setSelectedRegion(null);
          setSelectedSegment(null);
          persistRegionSegment(null, null);
        }
      }
    },
    [findBuByIdInProfile, setLocalStorage, profileData, persistRegionSegment]
  );

  const handleSelectRegion = useCallback(
    (region: string) => {
      setSelectedBU(null);
      localStorage.removeItem("selectedBU");
      setSelectedRegion(region);
      setSelectedSegment(null);
      persistRegionSegment(region, null);
    },
    [persistRegionSegment]
  );

  const handleSelectSegment = useCallback(
    (segment: string, region: string) => {
      setSelectedBU(null);
      localStorage.removeItem("selectedBU");
      setSelectedRegion(region);
      setSelectedSegment(segment);
      persistRegionSegment(region, segment);
    },
    [persistRegionSegment]
  );

  useEffect(() => {
    const oldSelected = localStorage.getItem("selectedBU");
    const fetchedBu = userProfile?.data?.data?.businessUnit;

    if (oldSelected && !isEmpty(oldSelected)) {
      setSelectedBU(JSON.parse(oldSelected));

      // Recuperar região e segmento armazenados no localStorage
      const storedRegion = localStorage.getItem("selectedRegion");
      const storedSegment = localStorage.getItem("selectedSegment");
      setSelectedRegion(storedRegion || null);
      setSelectedSegment(storedSegment || null);

      return;
    }

    // Caso não haja uma BU armazenada, mas haja uma região armazenada,
    // isso indica que o usuário está em modo apenas região.
    const storedRegion = localStorage.getItem("selectedRegion");

    if (storedRegion) {
      setSelectedRegion(storedRegion);
      setSelectedSegment(localStorage.getItem("selectedSegment") || null);

      return;
    }

    if (!fetchedBu) return;

    setLocalStorage(fetchedBu);
    setSelectedBU(fetchedBu);

    // Detectar região e segmento para a BU padrão
    const classification = userProfile?.data?.data?.businessUnitClassification;
    const location = findRegionAndSegmentForBusinessUnit(
      fetchedBu.id,
      classification
    );

    if (location) {
      setSelectedRegion(location.region);
      setSelectedSegment(location.segment);
      persistRegionSegment(location.region, location.segment);
    }
  }, [setLocalStorage, userProfile?.data?.data, persistRegionSegment]);

  return (
    <SelectedBuContext.Provider
      value={{
        handleSelectBu,
        handleSelectRegion,
        handleSelectSegment,
        selectedBU,
        selectedRegion,
        selectedSegment
      }}
    >
      {children}
    </SelectedBuContext.Provider>
  );
};

export const useSelectedBu = () => {
  const context = useContext(SelectedBuContext);

  return context;
};
